import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@attendance/ui/components/Button';
import i18n from '../../lib/i18n.js';
import { api } from '../../lib/api.js';

interface State {
  error: Error | null;
  /** Short code shown on screen AND written into the server log, so a phone
   *  call ("reference HDR-M4K2P-8QF3") lands on the right log line. */
  reference: string | null;
}

/**
 * A support reference, not an error id: it exists to be read aloud and typed
 * into a log search. Base-36 time gives it rough ordering, four random chars
 * keep two people crashing in the same second apart, and the whole thing is
 * uppercase and short enough to survive being written on paper.
 */
function makeReference(): string {
  const stamp = Date.now().toString(36).toUpperCase().slice(-5);
  const salt = Math.random().toString(36).toUpperCase().slice(2, 6).padEnd(4, 'X');
  return `HDR-${stamp}-${salt}`;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null, reference: null };

  static getDerivedStateFromError(error: Error): State {
    return { error, reference: makeReference() };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
     
    console.error('ErrorBoundary caught', error, info);
    // Best-effort report to the backend so prod errors land in server logs.
    // Silenced on failure — we don't want the boundary to recurse on its own
    // logging mishap.
    void api
      .post('/system/log-error', {
        // The reference rides in the message because that is the only free-text
        // field the crash endpoint accepts — and a reference the user can read
        // out is worthless if it appears nowhere in the server log.
        message: `[${this.state.reference ?? makeReference()}] ${error.message}`,
        stack: error.stack,
        componentStack: info.componentStack ?? undefined,
        url: typeof window !== 'undefined' ? window.location.href : undefined,
        userAgent:
          typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      })
      .catch(() => {
        /* swallow — see comment above */
      });
  }

  override render(): ReactNode {
    if (this.state.error) {
      // A class component can't use the `useTranslation` hook, and this
      // boundary sits ABOVE every provider on purpose — so it reads the i18n
      // singleton directly (initialised at the entry point, in main.tsx).
      const t = i18n.t.bind(i18n);
      const reference = this.state.reference ?? '—';
      return (
        <div className="flex min-h-screen items-center justify-center p-gutter-sm md:p-gutter">
          {/* `max-w-form`, not `max-w-lg`. This is a reading column, and the
              width system now has a role for one — `max-w-lg` was one of four
              different numbers screens had reached for when they wanted "not
              the full 1600". */}
          <div className="max-w-form space-y-inline">
            {/* `text-page-title`. This boundary sits above every provider and
                replaces the whole viewport, so it IS the page — and a page title
                is 34px on all 48 of the other screens. `text-h2` made the one
                screen a user sees when the app has failed the only screen whose
                heading is smaller than every other screen's. */}
            <h1 className="text-page-title text-foreground">
              {t('errorBoundary.title', 'This screen could not be opened')}
            </h1>
            {/* What the reader needs is what it means for their work, not what
                the module loader called the file. The raw message stays one
                click away for whoever is on the phone with them. */}
            <p className="text-copy text-muted-foreground">
              {t(
                'errorBoundary.body',
                'Something went wrong while loading this page. Nothing you entered has been saved or changed. Reloading usually fixes it — if it keeps happening, contact your administrator and quote the reference below.'
              )}
            </p>
            <p className="text-copy-sm text-muted-foreground">
              {t('errorBoundary.reference', 'Reference')}{' '}
              <span className="font-mono font-semibold text-foreground">{reference}</span>
            </p>
            {/* `Button`'s own `default` variant already IS the primary-brand,
                floor-not-height, coarse-pointer-bumped, focus-ringed control
                this used to hand-roll one property at a time — it has no
                provider/context dependency, so it is safe to use above every
                provider the way this boundary sits. */}
            <Button type="button" onClick={() => window.location.reload()}>
              {t('errorBoundary.reload', 'Reload page')}
            </Button>
            <details className="text-copy-sm text-muted-foreground">
              <summary className="cursor-pointer">
                {t('errorBoundary.technical', 'Technical details')}
              </summary>
              {/* `rounded-card` (12px) stays: a nested block inside the
                  disclosure, not a top-level surface. `bg-surface-2` names the
                  recessed-fill role that `--muted` aliases. */}
              <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-card bg-surface-2 p-3 text-copy-sm text-muted-foreground">
                {this.state.error.message}
              </pre>
            </details>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
